// api.js - Simplified & Optimized version
import axios from "axios";
import CustomToast from "component/Toast";
import { store } from "index";
import { useState, useRef } from "react";
import { baseApi } from "utils/constants/config";
import { deleteNullinObj } from "utils/helper/data";

axios.interceptors.request.use(
    (config) => {
        var state = store.getState();
        var token = state.auth.user.access_token;

        if (token) {
            config.headers["Authorization"] = token;
        }
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

export const handleError = (e, isHandleEmpty = false) => {
    // Ignore aborted requests - NO error toast
    if (axios.isCancel(e) || e.code === 'ERR_CANCELED' || e.name === 'AbortError') {
        console.log('Request cancelled');
        return;
    }
    
    if (e.response) {
        var { status } = e.response;
        if (status === 422) {
            if (e.response.data && e.response.data.errors) {
                const { errors } = e.response.data;
                for (const attribute in errors) {
                    if (errors.hasOwnProperty(attribute))
                        CustomToast.error(errors[attribute][0]);
                }
            }
        } else if (status === 403) {
            var url = e.response.config.url.split("/").pop();
            if (localStorage.getItem(url)) {
                if (e.response.config.method.toLowerCase() !== "get") {
                    CustomToast.error(
                        "Sorry, you are not authorized to perform this action",
                        5
                    );
                } else {
                    CustomToast.error(
                        "Sorry, you are not authorized to access this page",
                        5
                    );
                }
            } else {
                localStorage.setItem(url, true);
                if (e.response.config.method.toLowerCase() === "get") {
                    window.location.reload();
                }
            }
        } else if (isHandleEmpty) {
            if (status === 404) {
                console.log(e.response.data.message)
            }
        } else if (status >= 400 && status < 500) {
            CustomToast.error(e.response.data.message);
        } else {
            CustomToast.error(e.message);
        }
    } else if (e.request) {
        CustomToast.error(e.message);
    } else {
        CustomToast.error(e.message);
    }
};

const CustomException = (e) => {
    const error = new Error(e);
    error.response = e.response;
    return error;
};

export const RequestApi = async (
    url,
    method = "get",
    data = {},
    msg = "",
    header = null,
    isHandleEmpty = false,
    signal = null
) => {
    try {
        var Param = {
            method,
            url: baseApi + "/" + url,
            data,
            signal
        };
        if (header) {
            Param.headers = header;
        }

        const req = await axios(Param);
        localStorage.removeItem(baseApi + "/" + url);
        if (msg) CustomToast.success(msg);
        return req;
    } catch (e) {
        if (!axios.isCancel(e) && e.code !== 'ERR_CANCELED' && e.name !== 'AbortError') {
            if (msg !== null) handleError(e, isHandleEmpty);
            throw CustomException(e);
        }
        throw e; // Re-throw cancelled error for handling in useAPI
    }
};

export const useAPI = (url, { method = "get", message = "", isHandleEmpty = false, abortDuplicate = true } = {}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);
    const lastRequestKeyRef = useRef(null);

    const generateRequestKey = (params, id, data) => {
        return JSON.stringify({
            url: id ? `${url}/${id}` : url,
            method,
            params: deleteNullinObj(params || {}),
            data: deleteNullinObj(data || {})
        });
    };

    const fetchData = async ({ params = null, id = null, data = {} } = {}) => {
        const requestKey = generateRequestKey(params, id, data);

        // Abort previous request if it's the same and still loading
        if (abortDuplicate && loading && lastRequestKeyRef.current === requestKey) {
            console.log('Aborting duplicate request');
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        }

        // Update last request key
        lastRequestKeyRef.current = requestKey;

        // Create new abort controller
        if (abortControllerRef.current) {
            try {
                abortControllerRef.current.abort();
            } catch (e) {}
        }
        
        abortControllerRef.current = new AbortController();
        setLoading(true);
        
        try {
            var URL = url;
            if (id) {
                URL = url + "/" + id;
            }
            if (params) {
                var urlParam = new URLSearchParams(
                    deleteNullinObj(params)
                ).toString();
                URL = url + "?" + urlParam;
            }
            
            var req = await RequestApi(
                URL, 
                method, 
                data, 
                message, 
                null, 
                isHandleEmpty,
                abortControllerRef.current.signal
            );

            setLoading(false);
            
            if (!req?.data) {
                throw new Error('No data received');
            }
            
            // Reset key on success
            if (lastRequestKeyRef.current === requestKey) {
                lastRequestKeyRef.current = null;
            }
            
            return req.data;
        } catch (e) {
            // Handle cancelled request silently
            if (e.message === 'Request cancelled' || 
                axios.isCancel(e) || 
                e.code === 'ERR_CANCELED' || 
                e.name === 'AbortError') {
                //console.log('Request was cancelled:', requestKey);
                return null;
            }
            
            setLoading(false);
            setError(e);
            console.error('API Error:', e);
            throw e;
        }
    };

    const abort = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            lastRequestKeyRef.current = null;
            setLoading(false);
        }
    };

    return {
        loading,
        error,
        setLoading,
        fetch: fetchData,
        abort
    };
};